import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PasswordInput from '../components/PasswordInput';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';
import { FcGoogle } from 'react-icons/fc';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BookPage
      eyebrow="Account"
      title="Welcome back."
      intro="Step back into the room, pick up the thread, and keep going."
      bodyClassName="mx-auto max-w-md space-y-8"
    >
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[color:var(--wf-brass)]">Sign In</p>
        <h2 className="mt-3 font-serif text-3xl text-[color:var(--wf-ink)]">Enter your credentials.</h2>
        <p className="mt-2 text-sm leading-7 text-[color:var(--wf-ink-soft)]">
          Access your account to comment, keep up with new writing, and continue the conversation.
        </p>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <Input
            id="email-address"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PasswordInput
            id="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && (
          <div className="rounded-[0.8rem] border border-red-200 bg-red-50/80 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <Button type="submit" isLoading={isLoading} fullWidth size="large">
          Sign in
        </Button>
      </form>

      <div className="relative border-t border-[color:var(--wf-border)] pt-8">
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 bg-[color:var(--wf-paper)] px-3 text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">
          Or continue with
        </div>
      </div>

      <button
        type="button"
        onClick={loginWithGoogle}
        className="flex w-full items-center justify-center border border-[color:var(--wf-border-strong)] bg-[rgba(255,255,255,0.22)] px-4 py-3 text-sm font-medium text-[color:var(--wf-ink)] transition-colors duration-200 hover:border-[color:var(--wf-brass)] hover:bg-[rgba(255,255,255,0.32)] focus:outline-none focus:ring-2 focus:ring-[color:var(--wf-plum)]/20"
      >
        <FcGoogle className="mr-2 h-5 w-5" />
        Sign in with Google
      </button>

      <div className="border-t border-[color:var(--wf-border)] pt-6 text-center text-sm text-[color:var(--wf-ink-soft)]">
        Don't have an account?{' '}
        <Link to="/register" className="font-medium text-[color:var(--wf-plum)] transition-colors duration-200 hover:text-[color:var(--wf-ink)]">
          Register here
        </Link>
      </div>
    </BookPage>
  );
};

export default LoginPage;
